import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ComponentLike } from '@glint/template';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import type {
	CustomMetricSetupForm,
	OnFormCloseFn,
} from 'webapp/components/custom-query/setup/common/form';
import CustomQuerySetupCommonFormComponent from 'webapp/components/custom-query/setup/common/form';
import type { Args as WizardBaseArgs } from 'webapp/components/custom-query/setup/wizard/base';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import type CustomQuery from 'webapp/models-custom/custom-query';

type WizardEligibleForm = CustomMetricSetupFormSheet | CustomMetricSetupFormApiMetricBuilder;

// Each form's `wizardComponent` narrows the `form` arg to its own concrete form type,
// so the union can't be invoked directly. Both wizards share `wizard/base`'s Args, so we
// expose the reference through a single ComponentLike matching that shared signature.
type WizardComponent = ComponentLike<{ Args: WizardBaseArgs }>;

interface Args {
	model: CustomQuery;
	origin: string;
	onFormClose: OnFormCloseFn;
	inPopup?: boolean;
	// in-page-only (proxied)
	fromWizard?: boolean;
	// in-popup-only (proxied)
	cloneQuery?: (query: CustomQuery) => void;
	controlIconsNode?: HTMLElement;
	openMetricDefinitionPopupOnInit?: boolean;
	viewOnly?: boolean;
}

interface CustomQuerySetupPossibleWizardComponentInterface {
	Args: Args;
	Blocks: { default: [] };
}

export default class CustomQuerySetupPossibleWizardComponent extends Component<CustomQuerySetupPossibleWizardComponentInterface> {
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;

	@tracked isWizardFlow = false;
	@tracked form: WizardEligibleForm;

	isNew = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isNew = this.args.model.isNew;

		this.args.model.builderId = String(this.args.model.sourceId);

		const form = this.mmCustomQueriesService.createSetupFormForCustomQuery(
			this.args.model,
		) as WizardEligibleForm;
		this.form = form;

		this.form.openMetricDefinitionPopupOnInit =
			this.args.openMetricDefinitionPopupOnInit ?? false;

		void form.initBuilder().then(() => {
			// select wizard flow as default only for new queries (without duplicated ones)
			if (
				isAlive(this) &&
				this.args.model.isNew &&
				!this.args.model.cloneFrom &&
				form.isWizardFlowSupported
			) {
				this.setWizardFlow(true);
			}
		});

		const source = this.args.model.sourceObj;
		if (this.args.model?.isNew && source) {
			this.analyticsService.event(TrackingEvent.QUERY_SOURCE_SELECTED, {
				datasource: source.sourceType.key,
			});
		}
	}

	get showSwitchToWizardFlowButton(): boolean {
		return this.form.isWizardFlowSupported;
	}

	get wizardComponentClass(): WizardComponent {
		return this.form.wizardComponent as unknown as WizardComponent;
	}

	private setWizardFlow(isWizardFlow: boolean): void {
		this.isWizardFlow = isWizardFlow;
		next(() => {
			schedule('afterRender', this, this.resizePopup);
		});
	}

	private resizePopup(): void {
		this.mmCustomQueriesService.repositionEditPopup();
	}

	@action
	toggleIsWizardFlow(): void {
		this.setWizardFlow(!this.isWizardFlow);

		this.form.onWizardFlowToggle(this.isWizardFlow);
	}

	@action
	afterWizardFinish(): void {
		const customQuery = this.args.model;
		this.args.onFormClose(customQuery, this.isNew);
	}

	@action
	onFormReInit(form: CustomMetricSetupForm): void {
		// when the form-base re-initializes form, we must update it here also
		this.form = form as WizardEligibleForm;
	}

	@action
	teardownView(): void {
		this.cleanupForm();
	}

	private cleanupForm(): void {
		if (this.form) {
			this.form.onFormUnload();
		}
	}
	<template>
		<div class="custom-metric-possible-wizard" {{willDestroy this.teardownView}}>
			{{#if this.form.isLoading}}
				<BarLoaderComponent @size="big" />
			{{else}}
				{{#if this.isWizardFlow}}
					{{#let (component this.wizardComponentClass) as |ComponentName|}}
						<ComponentName
							@model={{@model}}
							@origin={{@origin}}
							@form={{this.form}}
							@toggleWizardFlow={{this.toggleIsWizardFlow}}
							@afterQuerySave={{this.afterWizardFinish}}
						/>
					{{/let}}
				{{else}}
					<CustomQuerySetupCommonFormComponent
						@model={{@model}}
						@origin={{@origin}}
						@inPopup={{@inPopup}}
						@fromWizard={{@fromWizard}}
						@onFormClose={{@onFormClose}}
						@onFormReInit={{this.onFormReInit}}
						@preloadedForm={{this.form}}
						@toggleWizardFlow={{this.toggleIsWizardFlow}}
						@cloneQuery={{@cloneQuery}}
						@controlIconsNode={{@controlIconsNode}}
						@openMetricDefinitionPopupOnInit={{this.form.openMetricDefinitionPopupOnInit}}
						@viewOnly={{@viewOnly}}
					>
						{{yield}}
					</CustomQuerySetupCommonFormComponent>
				{{/if}}
			{{/if}}
		</div>
	</template>
}
